import { Input, InputNumber } from "antd";
import cn from "classnames";
import React, { useCallback, useMemo, useState } from "react";

import { Alert, Button } from "@skydio/rivet-ui";

import { ErrorBoundary } from "./ErrorBoundary";
import { SuspenseFallbackSpin } from "./SuspenseFallbackSpin";

import type { FC } from "react";
import type { SuspenseFallbackSpinProps } from "./SuspenseFallbackSpin";

const SuspenseAndErrorBoundary: FC<SuspenseFallbackSpinProps> = props => {
  const errorAlertElement = useMemo(
    () => (
      <Alert status="error">
        <Alert.Content>
          <Alert.Title>Uh oh, something went wrong.</Alert.Title>
          <Alert.Description>Please refresh your browser page.</Alert.Description>
        </Alert.Content>
      </Alert>
    ),
    []
  );

  return (
    <SuspenseFallbackSpin>
      <ErrorBoundary fallback={errorAlertElement}>{props.children}</ErrorBoundary>
    </SuspenseFallbackSpin>
  );
};

// Component that can be used to test the Error Boundary
export const ThrowError: FC = () => {
  const [error, setError] = useState<Error | null>(null);
  const onClick = useCallback(() => {
    setError(new Error("An Uncaught Error"));
  }, []);

  if (error) {
    throw error;
  }
  return (
    <Button variant="default" destructive onPress={onClick}>
      Click me to throw a error
    </Button>
  );
};

// Component that can be used to test the Suspense Boundary
export const Suspender: FC<{ floating?: boolean }> = ({ floating = false }) => {
  const [suspended, setSuspended] = useState(false);
  const [duration, setDuration] = useState(0);

  const inputGroupStyle = useMemo(() => ({ display: "flex", width: "225px" }), []);
  const inputNumberStyle = useMemo(() => ({ flexGrow: 2 }), []);

  const suspend = useCallback(() => {
    setSuspended(true);
    const timeout = setTimeout(() => setSuspended(false), duration * 1000);
    return () => clearTimeout(timeout);
  }, [duration]);

  if (suspended) {
    throw new Promise(() => {
      // throwing a promise will trigger the nearest suspense boundary. Isn't javascript fun :)
    });
  }

  return (
    <div
      className={cn(
        "inline-block rounded-lg bg-gray-900 p-2",
        "border border-solid border-gray-800",
        {
          "absolute top-[100px] right-[45px] z-[999]": floating,
        }
      )}
    >
      <Input.Group compact={true} style={inputGroupStyle}>
        <InputNumber<number>
          style={inputNumberStyle}
          addonBefore="for"
          addonAfter="s"
          onChange={setDuration}
        />
        <Button
          variant="primary"
          isDisabled={suspended || duration === 0}
          isLoading={suspended}
          onPress={suspend}
        >
          Suspend
        </Button>
      </Input.Group>
    </div>
  );
};

export default SuspenseAndErrorBoundary;
